import type { ReactElement } from "react";

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import {
  CheckCircle,
  Coins,
  Crown,
  Loader2,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session data from payment verification
 */
interface SessionData {
  type: "credit_purchase" | "subscription" | string;
  credits?: number;
  package?: string;
  amount?: number;
  plan?: string;
}

/**
 * PaymentSuccessPage Component
 *
 * Displays payment confirmation with details.
 */
const PaymentSuccessPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchSessionDetails = async (): Promise<void> => {
    try {
      const response = await axios.get<SessionData>(
        `${API}/v1/payments/session/${sessionId}`,
        { withCredentials: true }
      );
      setSessionData(response.data);
    } catch (error) {
      console.error("Error fetching session:", error);
      setError(
        "Could not verify your payment. Please check your account or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2
            className="w-12 h-12 animate-spin mb-4"
            style={{ color: "var(--st-primary)" }}
          />
          <p style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}>
            Confirming your payment...
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        {/* Success Icon */}
        <div className="mb-6">
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ background: "var(--st-border-light)" }}
          >
            <CheckCircle className="w-12 h-12" style={{ color: "var(--st-primary)" }} />
          </div>
        </div>

        {/* Title */}
        <h1
          className="text-2xl font-bold mb-3"
          style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
        >
          Payment Successful!
        </h1>

        {/* Details */}
        {sessionData ? (
          <div className="mb-8">
            {sessionData.type === "credit_purchase" ? (
              <div
                className="rounded-xl p-6 mb-6"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
                }}
              >
                <div
                  className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
                  style={{
                    background: "var(--st-bg-surface)",
                    boxShadow: "0 2px 8px rgba(255,140,97,0.2)",
                  }}
                >
                  <Coins className="w-7 h-7" style={{ color: "var(--st-primary)" }} />
                </div>
                <p
                  className="text-lg font-bold"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  {sessionData.credits} credit
                  {sessionData.credits !== 1 ? "s" : ""} added!
                </p>
                <p
                  className="text-sm mt-1"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  {sessionData.package} &middot; $
                  {sessionData.amount?.toFixed(2)}
                </p>
              </div>
            ) : sessionData.type === "subscription" ? (
              <div
                className="rounded-xl p-6 mb-6"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                  boxShadow: "0 4px 12px rgba(255,140,97,0.3)",
                }}
              >
                <div
                  className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.25)" }}
                >
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <p
                  className="text-lg font-bold text-white"
                  style={{ fontFamily: "Quicksand, sans-serif" }}
                >
                  Welcome to Premium!
                </p>
                <p
                  className="text-sm mt-1 text-white/80"
                  style={{ fontFamily: "Quicksand, sans-serif" }}
                >
                  Your {sessionData.plan} subscription is now active
                </p>
              </div>
            ) : (
              <p
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                Your payment has been processed successfully.
              </p>
            )}
          </div>
        ) : error ? (
          <div
            className="rounded-xl p-4 mb-8"
            style={{ background: "#FFF3CD", border: "1px solid #FFE69C" }}
          >
            <p
              className="text-sm"
              style={{ fontFamily: "Quicksand, sans-serif", color: "#856404" }}
            >
              {error}
            </p>
          </div>
        ) : (
          <p
            className="mb-8"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            Thank you for your purchase!
          </p>
        )}

        {/* Call to Action */}
        <div className="space-y-3">
          <button
            onClick={() => navigate("/create")}
            className="w-full py-3 px-4 rounded-xl font-bold transition-all hover:shadow-lg flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
              color: "white",
              fontFamily: "Quicksand, sans-serif"
            }}
          >
            <Sparkles className="w-5 h-5" />
            Create a Story
            <ArrowRight className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate("/library")}
            className="w-full py-3 px-4 rounded-xl font-semibold transition-all hover:shadow-md"
            style={{
              background: "var(--st-border-light)",
              color: "var(--st-text-heading)",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            Go to My Library
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default PaymentSuccessPage;
